import { z } from 'zod';

import type { FieldType, Filter, PaginationSort } from '@models';
import { RELATIVE_DATE_PRESET_OPTIONS } from '@models/date.const';
import type { DateRangeValue } from '@components/date-range';
import { filterDefinitions } from '@components/filters/filter-utils';

import { isCustomDateRange } from './relative-date';
import {
  coerceToStringArray,
  stringToBoolean,
  stringToInt,
  stringToNumber,
} from './zod';

/**
 * A single filter value's `typeName` decides how it crosses the query string.
 */
type FilterConverter = {
  /**
   * Throws on malformed input so the caller can skip just that field.
   */
  decode: (raw: unknown, field: Filter) => unknown;
  /**
   * Produces a value ready for `qs.stringify`.
   */
  encode: (value: unknown, field: Filter) => unknown;
};

/**
 * Round-trips a `DateRangeValue` losslessly: a relative preset stays a bare
 * token (`7d`), while a custom range becomes a `[after, before]` pair (an open
 * end serializes as an empty string and decodes back to `null`).
 */
const dateRangeCodec = z.codec(
  z.union([
    z.string().refine(key => Object.hasOwn(RELATIVE_DATE_PRESET_OPTIONS, key)),
    z.array(z.string()).max(2),
  ]),
  z.custom<DateRangeValue>(),
  {
    decode: input =>
      Array.isArray(input)
        ? ([input[0] ?? '', input[1] ? input[1] : null] as DateRangeValue)
        : (input as DateRangeValue),
    encode: value =>
      isCustomDateRange(value) ? [value[0], value[1] ?? ''] : value,
  },
);

const getEnumValues = (field: Filter): string[] | undefined => {
  if (field.typeName !== 'Enum' && field.typeName !== 'EnumList')
    return undefined;

  return field.enumValues?.map(value =>
    typeof value === 'string' ? value : value.value,
  );
};

const stringConverter: FilterConverter = {
  decode: raw => {
    if (typeof raw !== 'string') throw new Error('expected a string');
    return raw;
  },
  encode: value => value,
};

const enumConverter: FilterConverter = {
  decode: (raw, field) => {
    if (typeof raw !== 'string') throw new Error('expected a string');

    const allowed = getEnumValues(field);
    if (allowed && !allowed.includes(raw))
      throw new Error('unknown enum value');

    return raw;
  },
  encode: value => value,
};

const stringListConverter: FilterConverter = {
  decode: raw => coerceToStringArray.parse(raw),
  encode: value => value,
};

/**
 * Like {@link stringListConverter} but drops any member not present in the
 * field's `enumValues`, so an unknown value in the URL is ignored rather than
 * poisoning the whole list.
 */
const enumListConverter: FilterConverter = {
  decode: (raw, field) => {
    const items = coerceToStringArray.parse(raw) as string[];
    const allowed = getEnumValues(field);

    return allowed ? items.filter(item => allowed.includes(item)) : items;
  },
  encode: value => value,
};

const jsonConverter: FilterConverter = {
  decode: raw => {
    if (typeof raw !== 'string') throw new Error('expected a string');
    return JSON.parse(raw);
  },
  encode: value => JSON.stringify(value),
};

const codecConverter = (codec: z.ZodType): FilterConverter => ({
  decode: raw => z.decode(codec, raw),
  encode: value => z.encode(codec, value),
});

/**
 * Like {@link codecConverter} but also enforces the field's declared min/max, so
 * a hand-edited URL can't apply a bound the filter UI wouldn't let you enter.
 */
const boundedNumberConverter = (codec: z.ZodType): FilterConverter => ({
  decode: (raw, field) => {
    const value = z.decode(codec, raw);
    if (typeof value !== 'number' || !Number.isFinite(value))
      throw new Error('expected a finite number');

    const { maxValue, minValue } = field.validationOptions ?? {};
    if (minValue !== undefined && value < minValue)
      throw new Error('below the field minimum');
    if (maxValue !== undefined && value > maxValue)
      throw new Error('above the field maximum');

    return value;
  },
  encode: value => z.encode(codec, value),
});

const converters: Record<FieldType, FilterConverter> = {
  BigInt: boundedNumberConverter(stringToInt),
  Boolean: codecConverter(stringToBoolean),
  DateRange: codecConverter(dateRangeCodec),
  DateTime: stringConverter,
  Enum: enumConverter,
  EnumList: enumListConverter,
  Float: boundedNumberConverter(stringToNumber),
  Int: boundedNumberConverter(stringToInt),
  JSON: jsonConverter,
  KeyValue: jsonConverter,
  String: stringConverter,
  StringList: stringListConverter,
  TimePeriod: stringConverter,
};

const isEmpty = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

/**
 * Encodes filter state into query params, driven by each filter's `typeName`.
 * Writes every active (non-empty) filter — including values that happen to
 * equal a default — so the URL is a complete record of what's applied and a
 * cleared filter reads back as absent. Skips empty values and any whose
 * converter throws.
 *
 * Iterates the state (not the filter options), so params come out in the order
 * the filters were applied and any key without a matching option is ignored.
 */
export const serializeTableFilters = <F extends object>(
  filters: F | undefined,
  filterOptions: readonly Filter<keyof F & string>[],
): Record<string, unknown> => {
  const query: Record<string, unknown> = {};
  if (!filters) return query;

  const definitions = filterDefinitions(filterOptions);

  Object.entries(filters).forEach(([key, value]) => {
    const field = definitions.get(key);
    if (!field || isEmpty(value)) return;

    try {
      const encoded = converters[field.typeName ?? 'String'].encode(
        value,
        field,
      );
      if (!isEmpty(encoded)) query[key] = encoded;
    } catch {
      // A value that can't be encoded is dropped rather than corrupting the URL.
    }
  });

  return query;
};

/**
 * Decodes query params back into filter state. Iterates the query, keeping only
 * params that match a filter option, so the applied order survives a round trip
 * and unknown params are ignored; a param that fails to decode is skipped,
 * leaving that filter to fall back to its default.
 */
export const deserializeTableFilters = <F extends object>(
  query: Record<string, unknown>,
  filterOptions: readonly Filter<keyof F & string>[],
): Partial<F> => {
  const filters: Record<string, unknown> = {};
  const definitions = filterDefinitions(filterOptions);

  Object.entries(query).forEach(([key, raw]) => {
    const field = definitions.get(key);
    if (!field || raw === undefined) return;

    try {
      const decoded = converters[field.typeName ?? 'String'].decode(raw, field);
      if (!isEmpty(decoded)) filters[key] = decoded;
    } catch {
      // Invalid params are ignored so a hand-edited URL can't break the table.
    }
  });

  return filters as Partial<F>;
};

/**
 * Encodes a (possibly multi-column) sort as `sort[field]=bool`, matching the
 * shape the search endpoints already consume. The active sort is always
 * written — including a default one — so the URL honestly reflects it.
 */
export const serializeSort = (
  sort: PaginationSort | undefined,
): Record<string, unknown> => {
  if (!sort || Object.keys(sort).length === 0) return {};

  const encoded: Record<string, string> = {};
  for (const [field, desc] of Object.entries(sort)) {
    if (typeof desc === 'boolean')
      encoded[field] = z.encode(stringToBoolean, desc);
  }

  return Object.keys(encoded).length > 0 ? { sort: encoded } : {};
};

/**
 * Decodes the `sort` param, preserving column order and dropping any entry
 * outside `sortableKeys` or whose direction isn't a stringy boolean — the same
 * way an unknown filter param is ignored. Dropping the primary column of a
 * multi-column sort promotes the next one, so priority can shift; returns
 * `undefined` when nothing survives.
 */
export const deserializeSort = (
  query: Record<string, unknown>,
  sortableKeys: readonly string[],
): PaginationSort | undefined => {
  const raw = query.sort;
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw))
    return undefined;

  const sort: PaginationSort = {};
  for (const [field, value] of Object.entries(raw as Record<string, unknown>)) {
    if (sortableKeys.includes(field) && (value === 'true' || value === 'false'))
      sort[field] = z.decode(stringToBoolean, value);
  }

  return Object.keys(sort).length > 0 ? sort : undefined;
};

/**
 * A table's sortable column ids paired with the sort it falls back to.
 */
export interface SortDefinition<K extends string = string> {
  readonly fallback: PaginationSort<K>;
  readonly keys: readonly K[];
}

/**
 * Declares a table's sort contract, pairing its allow-list with the sort to fall
 * back on. `NoInfer` makes `keys` the only inference site, so a fallback literal
 * naming a column outside it won't compile; the runtime check covers the forms
 * that slip past that, since a stray fallback key 400s every load of the table.
 */
export const defineSort = <const K extends string>(
  keys: readonly K[],
  fallback: PaginationSort<NoInfer<K>>,
): SortDefinition<K> => {
  const stray = Object.keys(fallback).filter(key => !keys.includes(key as K));
  if (stray.length)
    throw new Error(
      `defineSort: fallback names [${stray}], absent from its allow-list [${keys}]`,
    );
  if (keys.length === 0 || Object.keys(fallback).length === 0)
    throw new Error(
      `defineSort: the allow-list [${keys}] and fallback [${Object.keys(
        fallback,
      )}] must both be non-empty`,
    );

  return { fallback, keys };
};

/**
 * A bidirectional map between a piece of state and the URL query string.
 */
export interface QueryCodec<T> {
  keys: readonly string[];
  serialize: (value: T) => Record<string, unknown>;
  deserialize: (query: Record<string, unknown>) => T;
}

/**
 * Filters plus sort — the state a list table reflects to the URL.
 */
export interface TableState<F extends object> {
  filters: F;
  sort: PaginationSort;
}

/**
 * Reserved query key marking a deliberately-emptied filter set. It lets a
 * shared or reloaded "all filters cleared" view stay empty, rather than falling
 * back to the defaults the way a pristine (paramless) URL does.
 */
export const EMPTY_FILTERS_FLAG = 'filters';

/**
 * The per-table configuration a {@link QueryCodec} is built from.
 */
export interface TableCodecOptions<F extends object> {
  defaultFilters: F;
  filterOptions: readonly Filter<keyof F & string>[];
  sort: SortDefinition;
}

/**
 * Builds a {@link QueryCodec} for a table from its filter options and defaults.
 * The URL is authoritative: `deserialize` returns exactly the filters present,
 * falling back to the defaults only for a pristine query. A fully-cleared set
 * serializes to the {@link EMPTY_FILTERS_FLAG} marker so it round-trips as empty
 * instead of reverting to the defaults.
 */
export const buildTableCodec = <F extends object>({
  defaultFilters,
  filterOptions,
  sort: sortDefinition,
}: TableCodecOptions<F>): QueryCodec<TableState<F>> => ({
  keys: [...filterOptions.map(field => field.key), 'sort', EMPTY_FILTERS_FLAG],
  serialize: ({ filters, sort }) => {
    const filterQuery = serializeTableFilters(filters, filterOptions);
    // Only mark an explicitly-emptied set when there are defaults to suppress;
    // without defaults an empty query already means "no filters".
    const clearedFromDefaults =
      Object.keys(filterQuery).length === 0 &&
      Object.keys(defaultFilters).length > 0;

    return {
      ...(clearedFromDefaults ? { [EMPTY_FILTERS_FLAG]: 'none' } : filterQuery),
      ...serializeSort(sort),
    };
  },
  deserialize: query => {
    const filters = deserializeTableFilters(query, filterOptions);
    const explicitlyEmpty = query[EMPTY_FILTERS_FLAG] === 'none';

    return {
      filters:
        Object.keys(filters).length > 0 || explicitlyEmpty
          ? (filters as F)
          : defaultFilters,
      sort:
        deserializeSort(query, sortDefinition.keys) ?? sortDefinition.fallback,
    };
  },
});
